import React from "react";

function Bar({
  h = 16,
  w = "100%",
  className = ""
}: {
  h?: number;
  w?: number | string;
  className?: string;
}) {
  return (
    <div
      className={`animate-pulse bg-slate-200 rounded ${className}`}
      style={{ height: h, width: w }}
    />
  );
}

/** Generic skeleton row — a thin bar followed by a wider bar, like a table row. */
function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <Bar h={14} w={180} />
      <Bar h={14} w={120} />
      <Bar h={14} w={80} className="ml-auto" />
    </div>
  );
}

/** Skeleton action bar — title + a button placeholder on the right */
function SkeletonHeader() {
  return (
    <div className="flex items-center justify-between mb-4">
      <Bar h={22} w={200} className="rounded-lg" />
      <Bar h={34} w={110} className="rounded-lg" />
    </div>
  );
}

/** Full list/table page skeleton */
export function SkeletonListPage({ rows = 5 }: { rows?: number }) {
  return (
    <div className="v-fade-in p-4">
      <SkeletonHeader />
      <div className="bg-white rounded-xl border border-slate-200 px-4 py-2">
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

/** Settings / form page skeleton — label + input rows stacked in a card */
export function SkeletonSettingsPage({ fields = 5 }: { fields?: number }) {
  return (
    <div className="v-fade-in p-4 space-y-6">
      <SkeletonHeader />
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        {Array.from({ length: fields }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Bar h={13} w={140} />
            <Bar h={38} w="100%" className="rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Editor page — wide form with sidebar feel (chat window, workflow editor) */
export function SkeletonEditorPage() {
  return (
    <div className="v-fade-in flex gap-4 p-4 h-full">
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <Bar h={22} w={220} className="rounded-lg" />
        <Bar h={14} w="80%" />
        <Bar h={38} w="100%" className="rounded-lg" />
        <Bar h={38} w="100%" className="rounded-lg" />
        <Bar h={120} w="100%" className="rounded-lg" />
        <Bar h={38} w={120} className="rounded-lg ml-auto" />
      </div>
      <div className="w-72 bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} h={36} w="100%" className="rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/** Simple inline skeleton — just a few stacked bars for smaller panels */
export function SkeletonPanel({ rows = 4 }: { rows?: number }) {
  return (
    <div className="v-fade-in space-y-3 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Bar key={i} h={38} w="100%" className="rounded-lg" />
      ))}
    </div>
  );
}
